import { LocationStrategy } from '@angular/common';
import { Injectable, isDevMode, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SignInCurrentUserResponse, UserSession } from '@asamco/base';
import { catchErrorSwal, catchRetryError, getRandomId, isNullOrWhitespace, showErrorSwal } from '@asamco/utils';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AsamcoAuthService } from '../auth/asamco-auth.service';
import { fixUrlConnectedApps } from '../menu/legacy-support/utils';
import { MenuService } from '../menu/menu.service';
import { fixInnerAppMenuItemMessages, getApplicationOidFromPath } from './i-frame-utils';
import { ofTypeOld, toPayload } from './iframe-message-rxjs';
import { IFrameMessages, MessageType } from './message-type.enum';
import { Message } from './message.interface';
import { RemoteAppService } from './remote-app.service';

/**
 * Service to communicate with inner shell.
 * Utilizes MessageChannel to send and receive messages to the inner app
 */
@Injectable({
	providedIn: 'root',
})
export class IFrameParentService implements OnDestroy {
	// TODO: add a mutationObserver to the iframe, so we can clean up, and don't leak memory.

	private readonly destroy$ = new Subject<void>();

	/**
	 * internal subject tht lives for the lifetime of the app
	 */
	private readonly incomingMessages = new Subject<Message>();

	// TODO: Cleanup would consist of making this n-arity as there
	// could be many iframes, and not lose the MessageEvent.source so that
	// we can do a proper call BACK.
	private childOrigin: string;
	private childWindow: Window;

	/**
	 * Observable<Message>. lives for the duration of the app. it will emit
	 * all messages that come from the inner app.
	 */
	public readonly incomingMessages$ = this.incomingMessages.asObservable();

	// Keeps track of the latest sign in.
	private _signIn$: BehaviorSubject<SignInCurrentUserResponse> = new BehaviorSubject<SignInCurrentUserResponse>(null);
	public signIn$: Observable<SignInCurrentUserResponse> = this._signIn$.asObservable();

	constructor(
		private locationStrategy: LocationStrategy,
		private router: Router,
		private remoteAppService: RemoteAppService,
		private auth: AsamcoAuthService,
		private menu: MenuService
	) {}

	public init() {
		this.listenToRequestUserSessionMessages();

		this.listenCopyToClipboardMessage();
		this.listenToMenuMessages();
		this.listenToModalMessages();
	}

	listenToModalMessages() {
		this.incomingMessages$.pipe(ofTypeOld(MessageType.modalOpen), toPayload()).subscribe(m => {
			// Would be better to use the classes configured in swall settings
			document.body.classList.add('swal2-shown', 'swal2-height-auto');
		});

		this.incomingMessages$.pipe(ofTypeOld(MessageType.modalClose), toPayload()).subscribe(m => {
			// Would be better to use the classes configured in swall settings
			document.body.classList.remove('swal2-shown', 'swal2-height-auto');
		});
	}

	listenToMenuMessages() {
		this.incomingMessages$.pipe(ofTypeOld(MessageType.setMenu), toPayload()).subscribe(m => {
			const applicationOid = getApplicationOidFromPath(this.locationStrategy);

			// add the '/remote-app/{app-oid}' as prefix to the url
			const menu = fixUrlConnectedApps(m.menu, `/remote-app/${applicationOid}`);

			this.menu.setMenu(menu);
		});
		this.incomingMessages$.pipe(ofTypeOld(MessageType.setMenuVisibility), toPayload()).subscribe(m => {
			this.menu.setVisible(m.isVisible);
		});
		this.incomingMessages$.pipe(ofTypeOld(MessageType.toggleMenuVisibility), toPayload()).subscribe(m => {
			this.menu.toggleVisibility();
		});
	}

	// Remove when all iframe apps are upgraded to version >= 1.1.11
	listenCopyToClipboardMessage() {
		this.incomingMessages$.pipe(ofTypeOld(MessageType.copyToClipboard), toPayload()).subscribe(m => {
			navigator.clipboard.writeText(m.data);
		});
	}

	/**
	 * Register the active iframe.
	 *
	 * @param childOrigin origin of the iframe, for ex. http://localhost:4201
	 */
	public registerChildOrigin(childOrigin: string) {
		this.childOrigin = childOrigin;
	}

	/**
	 * Register the active iframe.
	 *
	 * @param childWindow ref to the iframe.contentWindow object
	 */
	public registerChildWindow(childWindow: Window) {
		this.childWindow = childWindow;
	}

	/** Unregister the now-active-iframe, if any. */
	public unregisterChildWindow() {
		this.childOrigin = undefined;
		this.childWindow = undefined;
	}

	private listenToRequestUserSessionMessages() {
		this.incomingMessages$
			.pipe(
				ofTypeOld(MessageType.requestUserSession),
				map(message => getApplicationOidFromPath(this.locationStrategy)),
				switchMap(appOid =>
					this.remoteAppService
						.signInCurrentUser(appOid)
						.pipe(catchErrorSwal({ retry: true }))
						.pipe(
							catchRetryError(error => {
								// TODO: This whole logic should be moved to the remote-app-iframe component.
								// Makes more sense that way.
								this.router.navigateByUrl('/managementportal/dashboard');
								return EMPTY;
							})
						)
				),
				tap((signInResponse: SignInCurrentUserResponse) => {
					this._signIn$.next(signInResponse);
				}),
				map((signInResponse: SignInCurrentUserResponse) => {
					if (signInResponse.warning && !isNullOrWhitespace(signInResponse.warning)) {
						Swal.fire({
							icon: 'warning',
							text: signInResponse.warning,
						});
					}

					const userSession = JSON.parse(JSON.stringify(this.auth.userSession));

					// TODO: Hier wordt "de eerste" loggedInBackend ge-assumed?
					userSession.loggedInBackends[0].token = signInResponse.token;

					if (signInResponse.app.company !== null) {
						userSession.loggedInBackends[0].baseUrl = signInResponse.app.company.asamcoApiUrl + '/api/';
						userSession.loggedInBackends[0].odataBaseUrl = signInResponse.app.company.asamcoApiUrl + '/odata/';
					}
					userSession.backend = userSession.loggedInBackends[0];

					return userSession;
				}),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: userSession => {
					this.send({
						type: MessageType.userSession,
						payload: userSession,
					});
				},
				error: error => {
					showErrorSwal(error);
				}
			});
	}

	public receiveFromWindow(event: MessageEvent) {
		if (this.childOrigin === event.origin) {
			if (event.data.type === MessageType.sideMenu) {
				fixInnerAppMenuItemMessages(this.locationStrategy, event.data.payload);
			}

			this.incomingMessages.next(event.data);
		} else if (isDevMode()) {
			console.warn(`Parent app ignored message from origin ${event.origin}, expected ${this.childOrigin}.`);
			console.log(event);
		}
	}

	/**
	 * Sends an message to the inner app.
	 * this is also used to return information. (the button clicked in a swal for example, or the route to navigate to)
	 * @param message
	 */
	send<T extends keyof IFrameMessages>(message: { type: T; payload: IFrameMessages[T] }) {
		const messageToSend = {
			...message,
			payload: {
				...message.payload,
				actionId: getRandomId(),
			},
		};

		if (this.childOrigin && this.childWindow) {
			this.childWindow.postMessage(messageToSend, this.childOrigin);
		} else {
			console.warn(
				`Attempted to send message, but either childOrigin or childWindow not set [${this.childOrigin}|${this.childWindow}]`
			);
		}
	}

	/**
	 * Cleanup
	 */
	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
